import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PartnerStatus } from '../../constants/partners';

export interface PartnerBalance {
  // Signed sum of pending entries (not yet available)
  pending: string;
  // Signed sum of available, unsettled entries: what the next payout would be.
  // Can be negative when deductions exceed earnings.
  available: string;
  // Signed sum of entries already settled by payouts
  paid: string;
  // <= 0. Unsettled reversals of commission already paid out — already included in
  // `available`; show it, never subtract it again.
  deductions: string;
  partner: { id: string; name: string; mobile: string; status: PartnerStatus };
  entry_count: number;
}

export interface BalanceTotals {
  pending: string;
  available: string;
  paid: string;
  deductions: string;
}

export interface CommissionSummaryState {
  items: PartnerBalance[];
  totals: BalanceTotals;
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

const ZERO_TOTALS: BalanceTotals = { pending: '0.00', available: '0.00', paid: '0.00', deductions: '0.00' };

const initialState: CommissionSummaryState = {
  items: [],
  totals: ZERO_TOTALS,
  total: 0,
  limit: 50,
  offset: 0,
  loading: false,
  error: null,
};

const commissionSummarySlice = createSlice({
  name: 'commissionSummary',
  initialState,
  reducers: {
    setCommissionSummaryLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setCommissionSummaryError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCommissionSummaryPage: (
      state,
      action: PayloadAction<{
        items: PartnerBalance[];
        totals: BalanceTotals;
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      const { items, totals, total, limit, offset } = action.payload;
      state.items = items;
      state.totals = totals;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
  },
});

export const { setCommissionSummaryLoading, setCommissionSummaryError, setCommissionSummaryPage } =
  commissionSummarySlice.actions;

export default commissionSummarySlice.reducer;
